import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Award, CheckCircle2, XCircle, Calendar, GraduationCap, MapPin, User, IdCard, BookOpen } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const monthsEs = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

const formatDateEs = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.getDate().toString().padStart(2, "0")} de ${monthsEs[d.getMonth()]} de ${d.getFullYear()}`;
};

const VerifyCertificate = () => {
  const { code } = useParams();
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCert = async () => {
      try {
        const res = await axios.get(`${API}/verify/${code}`);
        setCert(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || "Certificado no encontrado");
      } finally {
        setLoading(false);
      }
    };
    fetchCert();
  }, [code]);

  return (
    <div className="min-h-screen bg-black">
      <Header />
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-24" data-testid="verify-page">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-white/60 mb-4">
            <span className="h-px w-12 bg-[#E50914]"></span>
            VERIFICACIÓN DE CERTIFICADO
            <span className="h-px w-12 bg-[#E50914]"></span>
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-white">
            Código: <span className="text-[#E50914]">{code}</span>
          </h1>
        </div>

        {loading && (
          <div className="text-white/60 text-center py-12" data-testid="verify-loading">
            Verificando autenticidad...
          </div>
        )}

        {!loading && error && (
          <Card className="bg-black border-red-500/40" data-testid="verify-error">
            <CardContent className="py-10 text-center">
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Certificado no válido</h2>
              <p className="text-white/60">{error}</p>
              <p className="text-white/40 text-sm mt-4">
                Si crees que esto es un error, contacta a soporte de ContaFlix Pro.
              </p>
            </CardContent>
          </Card>
        )}

        {!loading && cert && cert.valid === false && (
          <Card className="bg-black border-red-500/40" data-testid="verify-revoked">
            <CardContent className="py-10 text-center">
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Certificado Revocado</h2>
              <p className="text-white/60">{cert.reason || "Este certificado ya no es válido."}</p>
            </CardContent>
          </Card>
        )}

        {!loading && cert && cert.valid && (
          <Card className="bg-gradient-to-br from-black via-black to-[#1a0a0a] border-[#E50914]/40 shadow-[0_0_60px_rgba(229,9,20,0.15)]" data-testid="verify-valid">
            <CardHeader className="border-b border-white/10 pb-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="bg-[#E50914]/20 p-3 rounded-full">
                    <CheckCircle2 className="w-8 h-8 text-[#E50914]" />
                  </div>
                  <div>
                    <CardTitle className="text-white text-2xl">Certificado Válido</CardTitle>
                    <p className="text-white/60 text-sm">Emitido oficialmente por ContaFlix Pro</p>
                  </div>
                </div>
                <Badge className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                  AUTÉNTICO
                </Badge>
              </div>
            </CardHeader>

            <CardContent className="space-y-8 pt-8">
              {/* Estudiante */}
              <div>
                <p className="text-xs uppercase tracking-widest text-[#E50914] font-bold mb-2">
                  Otorgado a
                </p>
                <h2 className="text-3xl md:text-4xl font-black text-white" data-testid="cert-student-name">
                  {cert.student_name}
                </h2>
                <p className="text-white/60 text-sm mt-1 flex items-center gap-2">
                  <IdCard className="w-4 h-4" />
                  C.I. / Pasaporte: {cert.student_id_masked || "—"}
                </p>
              </div>

              {/* Curso */}
              <div className="border-t border-white/10 pt-6">
                <p className="text-xs uppercase tracking-widest text-[#E50914] font-bold mb-2">
                  Por haber completado el módulo
                </p>
                <h3 className="text-xl md:text-2xl font-bold text-white italic" data-testid="cert-course-title">
                  «{cert.course_title}»
                </h3>
                {cert.course_category && (
                  <Badge className="mt-3 bg-white/10 text-white border-white/20">
                    {cert.course_category}
                  </Badge>
                )}
              </div>

              {/* Detalles */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-white/10 pt-6">
                <div className="text-center md:text-left">
                  <p className="text-xs uppercase text-[#E50914] font-bold tracking-wider">Duración</p>
                  <p className="text-white font-semibold mt-1">{cert.hours}</p>
                </div>
                <div className="text-center md:text-left">
                  <p className="text-xs uppercase text-[#E50914] font-bold tracking-wider">Modalidad</p>
                  <p className="text-white font-semibold mt-1">{cert.modality}</p>
                </div>
                <div className="text-center md:text-left">
                  <p className="text-xs uppercase text-[#E50914] font-bold tracking-wider">Emitido</p>
                  <p className="text-white font-semibold mt-1">{formatDateEs(cert.issued_at)}</p>
                </div>
                <div className="text-center md:text-left">
                  <p className="text-xs uppercase text-[#E50914] font-bold tracking-wider">País</p>
                  <p className="text-white font-semibold mt-1">{cert.country}</p>
                </div>
              </div>

              {/* Temas */}
              {cert.course_topics && cert.course_topics.length > 0 && (
                <div className="border-t border-white/10 pt-6">
                  <p className="text-xs uppercase tracking-widest text-[#E50914] font-bold mb-3 flex items-center gap-2">
                    <BookOpen className="w-4 h-4" /> Temas de la capacitación
                  </p>
                  <ul className="grid sm:grid-cols-2 gap-2" data-testid="cert-topics">
                    {cert.course_topics.map((topic, idx) => (
                      <li key={idx} className="text-white/80 flex items-start gap-2 text-sm">
                        <span className="text-[#E50914] mt-1">▸</span>
                        <span>{topic}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Instructor */}
              <div className="border-t border-white/10 pt-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#E50914]/15 flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-[#E50914]" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-white/50">Firmado por</p>
                  <p className="text-white font-semibold">{cert.instructor_name}</p>
                  <p className="text-white/60 text-sm">{cert.instructor_position}</p>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="border-t border-white/10 pt-6 text-center">
                <p className="text-white/40 text-xs italic">
                  Certificado de asistencia emitido por ContaFlix Pro. No constituye título académico ni
                  certificación oficial del Estado ecuatoriano.
                </p>
                <Link
                  to="/"
                  className="inline-block mt-4 text-[#E50914] hover:text-white text-sm font-semibold transition-colors"
                  data-testid="back-home-link"
                >
                  ← Volver a contaflixpro.com
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default VerifyCertificate;
